import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminAuthService from '../services/adminAuthService';
import { logActivity } from '../services/adminActivityService';
import './LoginPage.css';

const LoginPage = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const check = async () => {
      try {
        if (await AdminAuthService.isAuthenticated()) {
          navigate(location.state?.redirectTo || '/dashboard', { replace: true });
        }
      } catch {}
    };
    check();
  }, [navigate, location]);

  useEffect(() => {
    if (location.state?.message) setError(location.state.message);
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await AdminAuthService.createEmailSession(email, password);
      localStorage.setItem('adminUserId', await AdminAuthService.getCurrentUserId());
      await logActivity({ action: 'login', resourceType: 'session', details: 'Admin logged in' });
      navigate(location.state?.redirectTo || '/dashboard', { replace: true });
    } catch (err) {
      setError(AdminAuthService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      {/* ── Glassy green backdrop ────────────────────── */}
      <div className="login-bg" aria-hidden="true">
        <span className="login-orb login-orb-1" />
        <span className="login-orb login-orb-2" />
        <span className="login-orb login-orb-3" />
        <span className="login-wave" />
      </div>

      <main className="login-stage">
        <div className="login-card">
          <img src="/logo192.png" alt="Bettle" className="login-logo" />

          <div className="login-header">
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">Log in to your admin account to get started.</p>
          </div>

          <form onSubmit={handleLogin} noValidate>
            {error && (
              <div className="login-error">
                <span>{error}</span>
                <button type="button" className="login-error-close" onClick={() => setError(null)} aria-label="Dismiss">
                  ×
                </button>
              </div>
            )}

            <div className="login-field">
              <label className="login-icon-tab" htmlFor="login-email" aria-label="Email address">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                required
                autoComplete="email"
                className="login-input"
              />
            </div>

            <div className="login-field">
              <label className="login-icon-tab" htmlFor="login-password" aria-label="Password">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </label>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                autoComplete="current-password"
                className="login-input login-input-password"
              />
              <button
                type="button"
                className="login-eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/>
                    <path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/>
                    <line x1="2" x2="22" y1="2" y2="22"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="login-btn-spinner" />
                  Signing in…
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>

          <div className="login-divider"><span>admin access only</span></div>

          <p className="login-note">
            Contact your system administrator if you need access.
          </p>
        </div>

        <div className="login-secure">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Secure Login
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
